import { proxy, useSnapshot } from 'valtio'
import { proxyMap, proxySet } from 'valtio/utils'

import {
  type Comment,
  type CommentType,
  commentTypes,
  mapDTOtoComment,
} from '../models/Comment'
import { listCommentReactions, listComments } from '../api/commentApi'
import { type RealtimeMessage } from '../models/RealtimeMessage'
import {
  getLiveId,
  getPresenterToken,
  getRole,
} from '../../global/blocs/roomInfo'
import { getLiveToken } from '../../global/blocs/useConnectInfo'
import { subscribe } from '../../realtime/realtime'
import {
  getOverlayState,
  removeOverlay,
} from '../../firebase/hooks/useOverlays'
import { ChyronType } from '../../firebase/model/chyron-model'

import {
  type ChatId,
  addUnreadMessage,
  isChatId,
  removeUnreadMessage,
  shouldMarkMessageUnread,
} from './chatVisibility'
import { getLocalName } from './useLocalName'

type ChatMessagePaginationState = {
  isFetchingNextPage: boolean
}

type ChatMessagesState = Readonly<{
  ids: Readonly<Record<CommentType, (number | undefined)[] | undefined>>
  messages: Map<number, Comment>
  reactedTo: Map<number, Set<string>> | undefined
  /** Only the chat types paginate; questions are fetched in one go. */
  pagination: Record<ChatId, ChatMessagePaginationState>
}>

const chatMessagesState = proxy<ChatMessagesState>({
  ids: {
    'speaker-chat': undefined,
    chat: undefined,
    question: undefined,
  },
  messages: proxyMap(),
  reactedTo: undefined,
  pagination: {
    chat: {
      isFetchingNextPage: false,
    },
    'speaker-chat': {
      isFetchingNextPage: false,
    },
  },
})

// ===== Fetching data =====

const getRemoteChatMessage = async (commentId: number) => {
  const response = await listComments({
    object_type: 'live',
    object_id: getLiveId(),
    include_reactions_p: true,
    include_reply_to_comments_p: true,
    presenter_token: getPresenterToken(),
    comment_id: commentId,
  })

  const dto = response.data.at(0)
  if (!dto) return
  // return if data is empty to avoid runtime errors
  const comment = mapDTOtoComment(dto)
  updateChatMessage(response.data[0].comment_type, commentId, comment)
}

const getRemoteReactions = async () => {
  const response = await listCommentReactions({
    object_id: getLiveId(),
    object_type: 'live',
    object_token: await getLiveToken(),
  })

  if (!chatMessagesState.reactedTo) {
    // @ts-expect-error — this is the initial write
    chatMessagesState.reactedTo = proxyMap()
  }

  chatMessagesState.reactedTo!.clear()

  response.data.forEach(({ comment_id, reaction_emoji }) => {
    const r = chatMessagesState.reactedTo!.get(comment_id)
    if (r) {
      r.add(reaction_emoji)
    } else {
      chatMessagesState.reactedTo!.set(comment_id, proxySet([reaction_emoji]))
    }
  })
}

const RESERVED_BOT_GREETING_MESSAGE_ID = -1
const RESERVED_WEBINAR_INFO_MESSAGE_ID = -3

let injectedMessages = 0

const PAGE_SIZE = 100
const getNextPage = async (type: ChatId) => {
  const messageIds = chatMessagesState.ids[type]

  let page
  if (messageIds?.length) {
    const lastUnfetched = messageIds.findLastIndex((v) => v === undefined)
    page = Math.floor(lastUnfetched / PAGE_SIZE)
  } else {
    page = -1 // first page
  }

  chatMessagesState.pagination[type].isFetchingNextPage = true

  const response = await listComments({
    object_type: 'live',
    comment_type: type,
    object_id: getLiveId(),
    include_reactions_p: true,
    include_reply_to_comments_p: true,
    presenter_token: getPresenterToken(),
    prioritize_promoted_p: false,
    order: page === -1 ? 'desc' : 'asc',
    size: page === -1 ? PAGE_SIZE * 2 : PAGE_SIZE,
    p: page === -1 ? 1 : page + 1,
  })

  const comments = response.data.map(mapDTOtoComment)
  const currentPage = response.p

  if (!chatMessagesState.ids[type]) {
    // @ts-expect-error — this is the initial write
    chatMessagesState.ids[type] = new Array(response.total_count).fill(
      undefined
    )
  }

  // Detects id-count drift, e.g. reserved messages re-injected on a page-1 refetch
  const injectedMessagesForChatType = type === 'chat' ? injectedMessages : 0
  if (
    chatMessagesState.ids[type].length !==
    response.total_count + injectedMessagesForChatType
  ) {
    console.error(
      'Wrong message count in chat',
      type,
      chatMessagesState.ids[type].length,
      response.total_count,
      injectedMessagesForChatType
    )
  }

  comments.forEach((c) => chatMessagesState.messages.set(c.id, c))

  if (page === -1) {
    chatMessagesState.ids[type].splice(
      chatMessagesState.ids[type].length - comments.length,
      comments.length,
      ...comments.reverse().map((c) => c.id)
    )
  } else {
    chatMessagesState.ids[type].splice(
      page * PAGE_SIZE,
      comments.length,
      ...comments.map((c) => c.id)
    )
  }

  // Add reserved messages
  if (currentPage === 1) {
    switch (type) {
      case 'chat':
        if (comments.length === 0 && getRole() !== 'producer') {
          chatMessagesState.ids[type].push(RESERVED_BOT_GREETING_MESSAGE_ID)
          injectedMessages += 1
        }
        chatMessagesState.ids[type].push(RESERVED_WEBINAR_INFO_MESSAGE_ID)
        injectedMessages += 1
        break
      case 'speaker-chat':
      default:
    }
  }

  chatMessagesState.pagination[type].isFetchingNextPage = false
}

// Questions have no pagination: the list is server-ordered (`comment/set-order`)
// and producers reorder it by hand, so it is fetched whole.
const QUESTIONS_PAGE_SIZE = 5000

const fetchQuestions = async () => {
  const response = await listComments({
    object_type: 'live',
    comment_type: 'question',
    object_id: getLiveId(),
    include_reply_to_comments_p: true,
    presenter_token: getPresenterToken(),
    prioritize_promoted_p: false,
    size: QUESTIONS_PAGE_SIZE,
    p: 1,
  })

  const comments = response.data.map(mapDTOtoComment)
  comments.forEach((c) => chatMessagesState.messages.set(c.id, c))

  const fetched = comments.map((c) => c.id)
  const known = new Set(fetched)
  // Questions added by realtime while this request was in flight predate the
  // response, so they have to be merged rather than overwritten.
  const pending = (chatMessagesState.ids.question ?? []).filter(
    (id): id is number => id !== undefined && !known.has(id)
  )

  // @ts-expect-error — this is the initial write
  chatMessagesState.ids.question = [...pending, ...fetched]
}

const reorderQuestions = (order: number[]) => {
  const ids = chatMessagesState.ids.question
  if (!ids) return

  const known = new Set(order)
  const rest = ids.filter((id) => id !== undefined && !known.has(id))

  ids.splice(0, ids.length, ...order, ...rest)
}

// ===== Pagination  =====

const isFetchingNextPage = (type: ChatId) =>
  chatMessagesState.pagination[type].isFetchingNextPage

// ====================

const updateMessageId = (type: ChatId, commentId: number, newId: number) => {
  if (chatMessagesState.ids[type]) {
    const oldCommentIdIndex = chatMessagesState.ids[type].lastIndexOf(commentId)

    if (oldCommentIdIndex !== -1) {
      if (!chatMessagesState.ids[type].includes(newId)) {
        chatMessagesState.ids[type][oldCommentIdIndex] = newId
      } else {
        // If the newId already exists, we need to remove the old commentId
        chatMessagesState.ids[type].splice(oldCommentIdIndex, 1)
      }
    }
  }

  const oldCommentContent = chatMessagesState.messages.get(commentId)
  if (oldCommentContent) {
    chatMessagesState.messages.set(newId, {
      ...oldCommentContent,
      isOptimisticUpdate: false,
    })
    chatMessagesState.messages.delete(commentId)
  }

  const oldCommentReactions = chatMessagesState.reactedTo?.get(commentId)
  if (oldCommentReactions) {
    chatMessagesState.reactedTo?.set(newId, oldCommentReactions)
    chatMessagesState.reactedTo?.delete(commentId)
  }
}

const addMessage = async (type: ChatId, message: string, replyId?: number) => {
  const { senderName, fullName } = getLocalName()

  const replyMessageContent = replyId
    ? chatMessagesState.messages.get(replyId)
    : undefined

  const id = Math.random()

  const ids = chatMessagesState.ids[type]

  if (ids) {
    ids.push(id)
    chatMessagesState.messages.set(id, {
      id,
      htmlContent: message,
      textContent: message,
      name: senderName,
      fullName,
      pinned: false,
      status: '',
      label: getRole() === 'audience' ? 'audience' : 'speaker',
      reactions: proxyMap(),
      reply:
        replyId && replyMessageContent
          ? {
              commentId: replyId,
              name: replyMessageContent.name,
              fullName: replyMessageContent.fullName,
              label: replyMessageContent.label,
              content: replyMessageContent.textContent,
            }
          : undefined,
      isOptimisticUpdate: true,
    })
  }

  return id
}

const deleteMessage = (commentId: number) => {
  const overlay = getOverlayState()
  if (overlay?.type === ChyronType.Question && overlay.id === commentId) {
    void removeOverlay()
  }

  commentTypes.forEach((type) => {
    const ids = chatMessagesState.ids[type]
    const index = ids?.lastIndexOf(commentId) ?? -1
    if (index !== -1) {
      ids?.splice(index, 1)
    }
  })

  chatMessagesState.messages.delete(commentId)
  chatMessagesState.reactedTo?.delete(commentId)
}

const pinMessage = (commentId: number, pin: boolean) => {
  const comment = chatMessagesState.messages.get(commentId)
  if (comment) comment.pinned = pin
}

const addReaction = (commentId: number, emoji: string) => {
  // Update the reactions of the current user
  const { reactedTo } = chatMessagesState
  if (reactedTo) {
    const commentReactions = reactedTo.get(commentId)
    if (commentReactions) {
      commentReactions.add(emoji)
    } else {
      reactedTo.set(commentId, proxySet(emoji))
    }
  }

  // Update the reactions of the message
  const message = chatMessagesState.messages.get(commentId)
  message?.reactions.set(emoji, (message.reactions.get(emoji) ?? 0) + 1)
}

const removeReaction = (commentId: number, emoji: string) => {
  // Update the reactions of the current user
  chatMessagesState.reactedTo?.get(commentId)?.delete(emoji)

  // Update the reactions of the message
  const message = chatMessagesState.messages.get(commentId)
  const newValue = (message?.reactions.get(emoji) ?? 1) - 1
  if (newValue > 0) {
    message?.reactions.set(emoji, newValue)
  } else {
    message?.reactions.delete(emoji)
  }
}

const updateChatMessage = (
  type: CommentType,
  messageId: number,
  message: Comment
) => {
  chatMessagesState.messages.set(messageId, message)
  const ids = chatMessagesState.ids[type]

  // Questions list newest-first; chat reads oldest-first. The question branch
  // replaces the array rather than unshifting: valtio rewrites (and notifies on)
  // every index of a proxy array on unshift. It also runs before the initial
  // fetch resolves, which fetchQuestions then merges on top of.
  if (type === 'question') {
    if (ids?.includes(messageId)) return
    // @ts-expect-error — whole-array replacement, as in fetchQuestions
    chatMessagesState.ids.question = [messageId, ...(ids ?? [])]
    return
  }

  if (!ids || ids.includes(messageId)) return
  ids.push(messageId)
}

// ===== Hooks  =====

const initializedMessages = {
  chat: false,
  'speaker-chat': false,
}

const useChatMessages = (type: ChatId) => {
  if (!chatMessagesState.ids[type] && !initializedMessages[type]) {
    subscribeToRealtimeUpdates()
    initializedMessages[type] = true
    getNextPage(type).catch(() => {
      initializedMessages[type] = false
    })
  }
  return useSnapshot(chatMessagesState).ids[type]
}

const useChatMessage = (commentId: number) => {
  return useSnapshot(chatMessagesState).messages.get(commentId)
}

let initializedQuestions = false
const useQuestionIds = () => {
  // Keyed only on the flag: realtime can populate ids.question before the panel
  // first opens, and the full list still has to be fetched.
  if (!initializedQuestions) {
    subscribeToRealtimeUpdates()
    initializedQuestions = true
    fetchQuestions().catch((error: unknown) => {
      console.error('Could not load questions', error)
      initializedQuestions = false
    })
  }
  return useSnapshot(chatMessagesState).ids.question
}

let initializedReactions = false
const useOwnReactions = (commentId: number) => {
  if (!chatMessagesState.reactedTo && !initializedReactions) {
    initializedReactions = true
    getRemoteReactions().catch(() => {
      initializedReactions = false
    })
  }
  return useSnapshot(chatMessagesState).reactedTo?.get(commentId)
}

const useHasNextPage = (type: ChatId) => {
  const messages = useSnapshot(chatMessagesState).ids[type]

  if (!messages) return false

  const firstEmptyMessage = messages.findLastIndex((c) => c === undefined)
  return firstEmptyMessage !== -1
}

// ===== Realtime  =====

let subscribed = false
/**
 * Idempotent. Unread questions are counted from realtime `add` events, so this
 * has to run even when the questions panel was never opened and chat is off
 * entirely (`comment_mode: 'none'` with `question_mode: 'ask'`).
 */
const subscribeToRealtimeUpdates = () => {
  if (subscribed) return

  subscribed = true
  subscribe('public', '/all_json', (message: string) => {
    const event = JSON.parse(message) as RealtimeMessage

    switch (event.event) {
      case 'comment_update':
        if (event.payload) {
          const { comment_id: commentId, comment_status: status } =
            event.payload
          if (status === 'answered' || status === 'dismissed') {
            removeUnreadMessage(+commentId)
          }
          void getRemoteChatMessage(+commentId)
        }
        break
      case 'comments_order':
        if (event.payload?.comment_type === 'question') {
          reorderQuestions(event.payload.order.map(Number))
        }
        break
      case 'promote':
        pinMessage(+event.comment_id, true)
        break
      case 'unpromote':
        pinMessage(+event.comment_id, false)
        break
      case 'add':
        if (event.comment_id) {
          // Questions mark unread unconditionally; the sidebar clears on open.
          if (
            event.comment_type === 'question' ||
            (isChatId(event.comment_type) &&
              shouldMarkMessageUnread(event.comment_type))
          ) {
            addUnreadMessage(event.comment_type, +event.comment_id)
          }
          // TODO: use message pages, instead of single messages
          void getRemoteChatMessage(+event.comment_id)
        }
        break
      case 'delete':
        removeUnreadMessage(+event.comment_id)
        deleteMessage(+event.comment_id)
        break
      default:
    }
  }).catch(() => {
    subscribed = false
  })
}

export {
  chatMessagesState,
  getNextPage,
  isFetchingNextPage,
  updateMessageId,
  addMessage,
  deleteMessage,
  pinMessage,
  addReaction,
  removeReaction,
  reorderQuestions,
  fetchQuestions,
  subscribeToRealtimeUpdates,
  useChatMessages,
  useChatMessage,
  useQuestionIds,
  useOwnReactions,
  useHasNextPage,
  RESERVED_WEBINAR_INFO_MESSAGE_ID,
  RESERVED_BOT_GREETING_MESSAGE_ID,
}
